import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  MapPin,
  ArrowRight,
  ImageOff,
} from "lucide-react";
import { http } from "../axios/axios";
import { GetImageUrl } from "../helpers/GetImageUrl";
import Pagination from "../component/Pagination";
import { Helmet } from "react-helmet-async";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");

  const [seo, setSeo] = useState(null);

  useEffect(() => {
    const fetchSeo = async () => {
      try {
        const { data } = await http.get("/seo/Project");
        setSeo(data.data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchSeo();
  }, []);

  const seoData = seo || {
    metaTitle: "",
    metaDescription: "",
    metaTags: [],
    schemaScript: null,
  };

  const schemaScript = seoData?.schemaScript
    ? typeof seoData.schemaScript === "string"
      ? seoData.schemaScript
      : JSON.stringify(seoData.schemaScript)
    : null;

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 30,
  });
  const { page, limit } = pagination;
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);

      try {
        const url =
          activeFilter === "all"
            ? `/projects/published?page=${page}&limit=${limit}`
            : `/projects/published?status=${activeFilter}&page=${page}&limit=${limit}`;

        const res = await http.get(url);

        setProjects(res.data.data || []);

        const p = res.data.pagination;

        setPagination({
          page: p.currentPage,
          totalPages: p.totalPages,
          total: p.total,
          limit: p.limit,
        });
      } catch (err) {
        console.error("Projects fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [activeFilter, page, limit]);

  const filters = [
    { label: "All", value: "all" },
    { label: "Upcoming", value: "upcoming" },
    { label: "Ongoing", value: "ongoing" },
    { label: "Completed", value: "completed" },
  ];

  return (
    <>
      <Helmet>
        <title>{seoData?.metaTitle}</title>

        <meta name="description" content={seoData?.metaDescription} />

        <meta
          name="keywords"
          content={
            Array.isArray(seoData?.metaTags)
              ? seoData?.metaTags.join(", ")
              : seoData?.metaTags
          }
        />

        {schemaScript && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: schemaScript }}
          />
        )}
      </Helmet>

      <div className="min-h-screen bg-white pb-20 font-sans">
        <header className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 py-20 px-6 text-center relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight font-serif">
              Our <span className="text-blue-400">Projects</span>
            </h1>

            <p className="mt-6 text-lg text-gray-300 leading-relaxed">
              Explore our portfolio of successfully completed security projects,
              including CCTV surveillance, access control systems, fire alarm
              installations, intrusion detection, perimeter protection, and
              integrated security solutions for commercial, industrial,
              government, and residential clients.
            </p>
          </div>
        </header>

        <nav
          className="sticky top-20 z-40 border-b border-gray-200 bg-white/95 px-4 py-4 backdrop-blur-md sm:px-6"
          aria-label="Project status filters"
        >
          <div className="mx-auto max-w-7xl">
            <div className="flex overflow-x-auto scrollbar-hide">
              <div className="mx-auto flex min-w-max flex-nowrap gap-3 lg:min-w-0 lg:flex-wrap lg:justify-center">
                {filters.map(({ label, value }) => {
                  const isActive = activeFilter === value;

                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setActiveFilter(value);
                        setPagination((prev) => ({
                          ...prev,
                          page: 1,
                        }));
                      }}
                      aria-pressed={isActive}
                      aria-label={`Show ${label.toLowerCase()} projects`}
                      className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-all duration-300 sm:px-6 sm:py-2.5 sm:text-sm ${
                        isActive
                          ? "border-blue-600 bg-blue-600 text-white shadow-md"
                          : "border-gray-300 bg-white text-gray-700 hover:border-blue-600 hover:text-blue-600"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </nav>

        <section aria-labelledby="projects-list-heading">
          <h2 id="projects-list-heading" className="sr-only">
            Security Projects Portfolio
          </h2>

          {loading ? (
            <div
              className="flex flex-col items-center justify-center py-32"
              aria-live="polite"
              aria-busy="true"
            >
              <Loader2
                size={48}
                className="animate-spin text-blue-600"
                aria-hidden="true"
              />
              <p className="mt-4 text-sm text-gray-500">
                Loading security projects ...
              </p>
            </div>
          ) : projects.length === 0 ? (
            <div className="py-24 text-center">
              <h3 className="text-2xl font-bold text-gray-900">
                No Projects Found
              </h3>
              <p className="mt-3 text-gray-500">
                There are currently no{" "}
                <span className="font-semibold">
                  {activeFilter.toLowerCase()}
                </span>{" "}
                projects available.
              </p>
            </div>
          ) : (
            <section
              className="px-4 py-8 md:px-6"
              aria-labelledby="projects-grid-heading"
            >
              {" "}
              <h2 id="projects-grid-heading" className="sr-only">
                Security Projects Portfolio
              </h2>
              <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {/* <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"> */}
                {projects.map((project, index) => (
                  <article
                    key={project._id}
                    aria-labelledby={`project-title-${project?._id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/projects/${project?.slug}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        navigate(`/projects/${project.slug}`);
                      }
                    }}
                    className="group block h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <div className="flex h-full flex-col overflow-hidden rounded border border-slate-300 bg-white transition-all duration-300 hover:shadow-lg">
                      <div className="relative h-60 w-full overflow-hidden bg-slate-100 sm:h-65">
                        {project.bannerImage ? (
                          <img
                            src={GetImageUrl(project.bannerImage)}
                            loading={index < 6 ? "eager" : "lazy"}
                            fetchPriority={index < 3 ? "high" : "auto"}
                            decoding="async"
                            alt={
                              project.bannerAlt ||
                              `${project.title} security project in ${project.location}`
                            }
                            className="block h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                            onError={(event) => {
                              event.currentTarget.src =
                                "https://placehold.co/450x350?text=No+Image";
                            }}
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <ImageOff className="h-20 w-20 text-slate-300" />
                          </div>
                        )}
                      </div>

                      <div className="flex flex-1 flex-col p-3 sm:p-4">
                        <div className="mb-1 flex min-h-5 flex-wrap items-center gap-x-2 gap-y-1">
                          {project.status && (
                            <span
                              className={`text-xs font-semibold ${
                                project.status === "completed"
                                  ? "text-green-700"
                                  : project.status === "upcoming"
                                    ? "text-amber-700"
                                    : "text-blue-700"
                              }`}
                            >
                              {project.status.charAt(0).toUpperCase() +
                                project.status.slice(1)}
                            </span>
                          )}
                          {project.category && (
                            <span className="text-xs text-slate-500">
                              {project.category}
                            </span>
                          )}
                        </div>

                        <h3
                          id={`project-title-${project._id}`}
                          className="mt-1 min-h-10.5 text-sm font-bold leading-snug text-slate-900 line-clamp-2 sm:text-base"
                        >
                          {project.title}
                        </h3>

                        <p className="mt-2 min-h-10 text-xs leading-relaxed text-slate-500 line-clamp-2 sm:text-sm">
                          {project.shortDescription || project.description}
                        </p>

                        {project.location && (
                          <address className="mt-2 flex items-center gap-1.5 text-xs text-slate-500 not-italic">
                            <MapPin size={14} aria-hidden="true" />
                            {project.location}
                          </address>
                        )}

                        <div className="mt-auto pt-3">
                          <div className="flex items-center justify-center gap-2 rounded bg-blue-600 p-2 text-white transition-colors hover:bg-blue-700">
                            <span className="text-xs font-semibold sm:text-sm">
                              View Project
                            </span>
                            <ArrowRight
                              size={15}
                              className="transition-transform group-hover:translate-x-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              {projects.length > 0 && (
                <Pagination
                  page={pagination?.page}
                  totalPages={pagination?.totalPages}
                  onPrevious={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: prev.page - 1,
                    }))
                  }
                  onNext={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: prev.page + 1,
                    }))
                  }
                />
              )}
            </section>
          )}
        </section>
      </div>
    </>
  );
};
export default Projects;
